import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API from "../services/api";
import {
  BarChart3,
  Bell,
  ChartNoAxesCombined,
  ChevronRight,
  CreditCard,
  Gamepad2,
  Gift,
  Landmark,
  LogOut,
  Menu,
  Plane,
  Settings,
  ShieldAlert,
  Star,
  Trophy,
  UserPlus,
  Users,
  Wallet
} from "lucide-react";
import BrandLogo from "./BrandLogo";
import { getAdminPermissions, getStoredAdmin } from "../utils/adminAuth";

const menuItems = [
  { label: "Dashboard",          path: "/admin",                  icon: BarChart3,             permission: "dashboard" },
  { label: "Users",              path: "/admin/users",            icon: Users,                 permission: "users" },
  { label: "Failed Deposits",     path: "/admin/deposits",         icon: Wallet,                permission: "withdraws" },
  { label: "Payouts",            path: "/admin/payouts",          icon: Landmark,              permission: "withdraws" },
  { label: "P&L Statement",      path: "/admin/pnl-statement",    icon: ChartNoAxesCombined,   permission: "dashboard" },
  { label: "Result",             path: "/admin/results",          icon: Trophy,                permission: "results" },
  {
    label: "Starline",
    path: "/admin/starline",
    icon: Star,
    permission: "results",
    children: [
      { label: "Declare Result",  path: "/admin/starline" },
      { label: "Game Control",    path: "/admin/starline-control" },
      { label: "Stake Analysis",  path: "/admin/starline-stake" },
    ],
  },
  {
    label: "Game Management",
    path: "/admin/markets",
    icon: Gamepad2,
    permission: "markets",
    children: [
      { label: "Game Control",    path: "/admin/markets" },
      { label: "Bid Revert",      path: "/admin/bid-revert" },
      { label: "Game Rates",      path: "/admin/game-rates" },
      { label: "Stake Analysis",  path: "/admin/stake-analysis" },
    ],
  },
  {
    label: "Bonus & Promo",
    path: "/admin/bonus",
    icon: Gift,
    permission: "settings",
    children: [
      { label: "Promotions",       path: "/admin/bonus" },
      { label: "Referral Bonus",   path: "/admin/referral-bonuses" },
    ],
  },
  { label: "Banners",            path: "/admin/banners",          icon: Gift,                  permission: "banners" },
  { label: "Payment Gateway",    path: "/admin/payment-gateway",  icon: CreditCard,            permission: "settings" },
  { label: "Send Notifications", path: "/admin/notifications",    icon: Bell,                  permission: "notifications" },
  {
    label: "Agent Program",
    path: "/admin/agents",
    icon: UserPlus,
    permission: "agents",
    children: [
      { label: "Agents",   path: "/admin/agents" },
    ],
  },
  {
    label: "Casino",
    path: "/admin/casino",
    icon: Plane,
    permission: "settings",
    children: [
      { label: "Bet History",  path: "/admin/casino" },
      { label: "Settings",     path: "/admin/casino/settings" },
    ],
  },
  { label: "Risk Monitor",        path: "/admin/risk-monitor",     icon: ShieldAlert,           permission: "users" },
  { label: "Sub Admins",          path: "/admin/sub-admins",       icon: UserPlus,              permission: "sub_admins" },
  { label: "Settings",           path: "/admin/settings",         icon: Settings,              permission: "settings" },
];

function AdminSidebar({ isMobile = false, isLaptop = false, isOpen = false, onClose = () => {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const admin = getStoredAdmin();
  const permissions = getAdminPermissions(admin);
  const [pendingPayouts, setPendingPayouts] = useState(0);

  useEffect(() => {
    let active = true;
    API.get("/admin/withdraws")
      .then((res) => {
        if (!active) return;
        const count = (Array.isArray(res.data) ? res.data : []).filter((item) => item.status === "pending").length;
        setPendingPayouts(count);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const isActive = (item) => {
    if (location.pathname === item.path) return true;
    return Array.isArray(item.children) && item.children.some((child) => location.pathname === child.path);
  };

  const sideStyle = isMobile
    ? { ...sidebar, ...mobileSidebar, transform: isOpen ? "translateX(0)" : "translateX(-105%)" }
    : isLaptop ? laptopSidebar : sidebar;

  const visibleItems = menuItems.filter((item) => permissions[item.permission] !== false).map((item) => {
    if (item.path === "/admin/payouts" && pendingPayouts > 0) return { ...item, count: pendingPayouts };
    return item;
  });

  const go = (path) => {
    navigate(path);
    if (isMobile) onClose();
  };

  return (
    <aside style={sideStyle}>
      {/* Brand */}
      <div style={brandRow}>
        <BrandLogo compact={false} size="sm" />
        {isMobile && (
          <button type="button" style={closeMobileBtn} onClick={onClose}>
            <Menu size={16} color="#9ca3af" />
          </button>
        )}
      </div>

      <div style={divider} />

      {/* Nav */}
      <nav style={nav}>
        {visibleItems.map((item) => {
          const active = isActive(item);
          const Icon = item.icon;
          return (
            <div key={item.label}>
              <button
                type="button"
                onClick={() => go(item.path)}
                style={active ? activeBtn : navBtn}
              >
                <Icon size={16} color={active ? "#fbbf24" : "rgba(255,255,255,0.5)"} />
                <span style={active ? activeBtnText : navBtnText}>{item.label}</span>
                {item.count ? <span style={badge}>{item.count}</span> : null}
                {item.children ? <ChevronRight size={12} color={active ? "#fbbf24" : "rgba(255,255,255,0.3)"} style={active ? { transform: "rotate(90deg)" } : {}} /> : null}
              </button>

              {active && Array.isArray(item.children) && (
                <div style={subNav}>
                  {item.children.map((child) => (
                    <button
                      key={child.path}
                      type="button"
                      onClick={() => go(child.path)}
                      style={location.pathname === child.path ? activeSubBtn : subBtn}
                    >
                      {child.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={footer}>
        <div style={footerDot} />
        <span style={footerText}>System Online</span>
      </div>

      <button
        type="button"
        style={logoutBtn}
        onClick={() => {
          localStorage.removeItem("admin");
          if (isMobile) onClose();
          navigate("/admin-login");
        }}
      >
        <LogOut size={14} color="#ef4444" />
        <span>Logout</span>
      </button>
    </aside>
  );
}

const base = {
  width: "220px",
  height: "100vh",
  background: "linear-gradient(180deg, #1e1b4b 0%, #312e81 100%)",
  position: "fixed",
  left: 0,
  top: 0,
  padding: "0 0 16px",
  boxSizing: "border-box",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: 0,
  borderRight: "1px solid rgba(255,255,255,0.06)",
  boxShadow: "4px 0 24px rgba(0,0,0,0.3)",
};
const sidebar = base;
const laptopSidebar = { ...base, width: "200px" };
const mobileSidebar = { ...base, width: "240px", maxWidth: "80vw", zIndex: 1000, transition: "transform 0.26s ease", boxShadow: "0 0 60px rgba(0,0,0,0.5)" };

const brandRow = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 14px 14px", background: "rgba(0,0,0,0.2)", borderBottom: "1px solid rgba(255,255,255,0.08)" };
const closeMobileBtn = { width: 32, height: 32, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.08)", borderRadius: 8, display: "grid", placeItems: "center", cursor: "pointer" };
const divider = { height: 1, background: "rgba(255,255,255,0.08)", margin: "0 0 8px" };

const nav = { display: "flex", flexDirection: "column", gap: 2, flex: 1, padding: "8px 10px 0" };

const navBtn = {
  width: "100%",
  height: 42,
  border: 0,
  background: "transparent",
  color: "rgba(255,255,255,0.65)",
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "0 12px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
  borderRadius: 10,
  textAlign: "left",
};
const activeBtn = {
  ...navBtn,
  background: "linear-gradient(135deg, rgba(249,115,22,0.25), rgba(234,88,12,0.15))",
  border: "1px solid rgba(249,115,22,0.35)",
  color: "#fbbf24",
};
const navBtnText = { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const activeBtnText = { ...navBtnText, fontWeight: 700 };
const badge = { background: "#ef4444", color: "#fff", borderRadius: 999, padding: "2px 7px", fontSize: 10, fontWeight: 800, flexShrink: 0 };

const subNav = { marginLeft: 22, marginBottom: 4, display: "flex", flexDirection: "column", gap: 1, borderLeft: "1px solid rgba(255,255,255,0.15)", paddingLeft: 12 };
const subBtn = { height: 34, border: 0, background: "transparent", textAlign: "left", padding: "0 10px", fontSize: 12, color: "rgba(255,255,255,0.5)", cursor: "pointer", borderRadius: 8, fontWeight: 500 };
const activeSubBtn = { ...subBtn, background: "rgba(249,115,22,0.15)", color: "#fbbf24", fontWeight: 700 };

const footer = { display: "flex", alignItems: "center", gap: 7, padding: "10px 14px 6px", marginTop: "auto" };
const footerDot = { width: 7, height: 7, borderRadius: "50%", background: "#22c55e", flexShrink: 0 };
const footerText = { fontSize: 11, color: "rgba(255,255,255,0.4)", fontWeight: 600 };

const logoutBtn = {
  width: "calc(100% - 20px)",
  margin: "8px 10px 0",
  height: 40,
  border: "1px solid rgba(239,68,68,0.3)",
  background: "rgba(239,68,68,0.1)",
  color: "#fca5a5",
  borderRadius: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
};

export default AdminSidebar;
